<template>
  <div class="login-wrapper">
    <div class="login-container">
      <!-- 添加logo -->
      <div style="text-align: center; margin-bottom: 1rem;">
        <img src="/images/logo.png" alt="愿毕达logo"
             style="width: 100px; height: 100px; border-radius: 50%;
             object-fit: contain; background-color: #fff;">
      </div>
      <h2>愿毕达高考志愿填报系统登录</h2>

      <!-- 登录方式切换 -->
      <div class="login-tabs">
        <button
          :class="['tab-btn', loginType === 'wechat' ? 'active' : '']"
          @click="switchToWechatLogin">
          微信登录
        </button>
        <button
          :class="['tab-btn', loginType === 'password' ? 'active' : '']"
          @click="loginType = 'password'">
          密码登录
        </button>
        <!-- 短信登录按钮（功能保留但隐藏） -->
        <button
          v-show="false"
          :class="['tab-btn', loginType === 'sms' ? 'active' : '']"
          :disabled="true"
          @click="handleSmsLoginClick">
          短信登录（暂未开放）
        </button>
      </div>

      <!-- 错误信息显示 -->
      <div v-if="error" class="error">{{ error }}</div>

      <!-- 微信登录 -->
      <div v-if="loginType === 'wechat'" class="wechat-login">
        <div v-if="wechatQrcodeUrl" class="qrcode-container">
          <div class="qrcode-image">
            <img :src="wechatQrcodeUrl" alt="微信登录二维码" class="qrcode-img">
          </div>
          <template v-if="!isMobile">
            <p class="qrcode-tip">请使用微信扫一扫登录或注册</p>
          </template>
          <template v-else>
            <p class="qrcode-tip">长按识别二维码登录</p>
          </template>
          <p v-if="wechatLoginStatus === 'scanned'" class="status-text scanned">已授权，正在登录...</p>
          <p v-if="wechatLoginStatus === 'expired'" class="status-text expired">二维码已过期，请刷新</p>
        </div>
        <div v-else class="qrcode-loading">
          <p>正在加载二维码...</p>
        </div>
        <button v-if="showDashboardButton" type="button" class="btn btn-dashboard" @click="$router.push('/dashboard')">前往Dashboard</button>
        <button type="button" class="btn btn-refresh" @click="refreshWechatQrcode">刷新二维码</button>
      </div>

      <!-- 密码登录表单 -->
      <form v-if="loginType === 'password'" @submit.prevent="passwordLogin">
        <input type="hidden" name="csrfmiddlewaretoken" :value="csrfToken">
        <div class="form-group">
          <label for="username">手机号</label>
          <input type="tel" id="username" v-model="username" placeholder="请输入手机号" required pattern="[0-9]{11}" title="请输入11位手机号">
        </div>
        <div class="form-group">
          <label for="password">密码</label>
          <input type="password" id="password" v-model="password" required autocomplete="current-password">
        </div>
        <button type="submit" class="btn">登录</button>
      </form>

      <!-- 短信登录表单 -->
      <form v-if="loginType === 'sms'" @submit.prevent="smsLogin">
        <input type="hidden" name="csrfmiddlewaretoken" :value="csrfToken">
        <div class="form-group">
          <label for="phone">手机号</label>
          <input type="tel" id="phone" v-model="smsPhone" placeholder="请输入手机号" required pattern="[0-9]{11}" title="请输入11位手机号">
        </div>
        <div class="form-group">
          <label for="smsCode">验证码</label>
          <div class="sms-code-group">
            <input type="text" id="smsCode" v-model="smsCode" placeholder="请输入验证码" required maxlength="6">
            <button
              type="button"
              class="send-code-btn"
              :disabled="countdown > 0 || !smsPhone"
              @click="sendSmsCode">
              {{ countdown > 0 ? `${countdown}秒后重发` : '发送验证码' }}
            </button>
          </div>
        </div>
        <button type="submit" class="btn">登录</button>
      </form>

      <div class="link">
        <div v-if="loginType === 'password'">
          没有账号？<a href="#" @click.prevent="$router.push('/register')">立即注册</a>
        </div>
        <div v-else-if="loginType === 'sms'">
          短信登录功能暂未开放
        </div>
        <div v-else>
          使用微信扫码快速登录
        </div>
      </div>
    </div>

    <!-- 新用户注册弹窗（高考年份选择） -->
    <div v-if="showRegisterModal" class="modal-overlay" @click.self="closeRegisterModal">
      <div class="modal-container">
        <h3>完善注册信息</h3>
        <div class="form-group">
          <label>高考年份 <span style="color: red;">*</span></label>
          <select v-model="registerGaokaoYear" class="form-select" required>
            <option value="">请选择高考年份</option>
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}年</option>
          </select>
          <p class="hint-text">注：高考年份一旦设置将不能修改，请谨慎选择（必填）</p>
        </div>
        <div class="modal-buttons">
          <button type="button" class="btn-cancel" @click="closeRegisterModal">取消</button>
          <button type="button" class="btn-confirm" @click="completeRegister">完成注册</button>
        </div>
      </div>
    </div>

    <!-- 微信登录完善信息弹窗（高考年份和手机号） -->
    <div v-if="showCompleteInfoModal" class="modal-overlay" @click.self="closeCompleteInfoModal">
      <div class="modal-container">
        <h3>完善个人信息</h3>
        <p class="modal-desc">首次登录请完善以下信息，用于筛选志愿数据</p>
        <div class="form-group">
          <label>高考年份 <span style="color: red;">*</span></label>
          <select v-model="completeInfoGaokaoYear" class="form-select" required>
            <option value="">请选择高考年份</option>
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}年</option>
          </select>
          <p class="hint-text">注：高考年份一旦设置将不能修改，请谨慎选择</p>
        </div>
        <div class="form-group">
          <label>手机号 <span style="color: red;">*</span></label>
          <input 
            type="tel" 
            v-model="completeInfoPhone" 
            placeholder="请输入手机号" 
            class="form-input"
            maxlength="11"
            pattern="[0-9]{11}"
            required>
          <p class="hint-text">手机号用于登录和接收重要通知</p>
        </div>
        <div class="modal-buttons">
          <button type="button" class="btn-confirm" @click="submitCompleteInfo">完成</button>
        </div>
      </div>
    </div>

    <!-- 设置密码弹窗 -->
    <div v-if="showSetPasswordModal" class="modal-overlay" @click.self="skipSetPassword">
      <div class="modal-container">
        <h3>设置登录密码</h3>
        <p class="modal-desc">设置密码后，您可以使用手机号和密码登录</p>
        <div class="form-group">
          <label>密码 <span style="color: red;">*</span></label>
          <input 
            type="password" 
            v-model="setPasswordValue" 
            placeholder="请设置6位以上密码" 
            class="form-input"
            minlength="6"
            required>
        </div>
        <div class="form-group">
          <label>确认密码 <span style="color: red;">*</span></label>
          <input 
            type="password" 
            v-model="confirmPasswordValue" 
            placeholder="请再次输入密码" 
            class="form-input"
            minlength="6"
            required>
        </div>
        <div class="modal-buttons">
          <button type="button" class="btn-cancel" @click="skipSetPassword">跳过</button>
          <button type="button" class="btn-confirm" @click="submitSetPassword">设置密码</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入axios库
import axios from 'axios';
import { getCookie } from '@/utils'


export default {
  data() {
    return {
      loginType: 'wechat',  // wechat, password, sms
      username: '',
      password: '',
      smsPhone: '',
      smsCode: '',
      error: '',
      csrfToken: '',
      countdown: 0,
      showRegisterModal: false,  // 显示注册弹窗
      registerGaokaoYear: '',  // 注册时选择的高考年份
      availableYears: [],  // 可用年份列表
      pendingPhone: '',  // 待注册的手机号
      pendingCode: '',  // 待使用的验证码
      // 微信登录相关
      wechatQrcodeUrl: '',  // 微信登录二维码URL
      wechatTicket: '',  // 微信登录ticket
      wechatAuthUrl: '',  // 微信授权URL
      wechatLoginStatus: '',  // 登录状态：pending, scanned, confirmed, expired
      wechatCheckInterval: null,  // 轮询定时器
      wechatAuthWindow: null,  // 授权窗口引用
      wechatAuthCheckInterval: null,  // 检查授权窗口是否关闭的定时器
      // 完善信息弹窗
      showCompleteInfoModal: false,  // 显示完善信息弹窗
      completeInfoGaokaoYear: '',  // 完善信息时选择的高考年份
      completeInfoPhone: '',  // 完善信息时填写的手机号
      // 设置密码弹窗
      showSetPasswordModal: false,  // 显示设置密码弹窗
      setPasswordValue: '',  // 设置的密码
      confirmPasswordValue: '',  // 确认的密码
      isNewUser: false,  // 是否为新用户
      isMobile: false,  // 是否为移动设备
      showDashboardButton: false  // 是否显示跳转到Dashboard的按钮
    };
  },
  mounted() {
    // 获取CSRF令牌
    this.csrfToken = getCookie('csrftoken');
    
    // 检测是否为移动设备
    this.handleResize();
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);

    // 生成可用年份列表（过去2年到未来3年，覆盖复读生和应届生需求）
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    const startYear = currentYear - 2;  // 包含过去2年（复读生）
    const endYear = currentYear + 3;    // 包含未来3年

    for (let year = startYear; year <= endYear; year++) {
      this.availableYears.push(year);
    }
    
    // 默认加载微信登录二维码
    if (this.loginType === 'wechat') {
      this.getWechatQrcode();
    }
    
    // 检查是否是从微信授权回调回来的
    this.checkWechatCallback();
  },
  methods: {
    handleSmsLoginClick() {
      // 短信登录暂未开放
      this.$message.info('短信登录功能暂未开放，请使用密码登录');
    },
    
    handleResize() {
      this.isMobile = window.innerWidth <= 768 || /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
    },

    switchToWechatLogin() {
      this.loginType = 'wechat';
      // 切换到微信登录时自动加载二维码
      this.getWechatQrcode();
    },

    async passwordLogin() {
      try {
        let csrftoken = getCookie('csrftoken');
        if (!csrftoken) {
          await axios.get('/');
          csrftoken = getCookie('csrftoken');
        }

        const response = await axios.post('api/login/', {
          username: this.username,
          password: this.password
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          await this.fetchUserInfo();
          this.$router.push('/dashboard');
        } else {
          this.error = response.data.error || '用户名或密码错误';
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          this.error = err.response.data.error;
        } else {
          this.error = '登录时发生错误，请稍后重试';
        }
      }
    },

    async sendSmsCode() {
      try {
        this.error = '';
        const response = await axios.post('api/send-sms-login-code/', {
          phone_number: this.smsPhone
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          this.startCountdown();
        } else {
          this.error = response.data.error || '发送验证码失败';
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          this.error = err.response.data.error;
        } else {
          this.error = '发送验证码失败，请稍后重试';
        }
      }
    },

    startCountdown() {
      this.countdown = 60;
      const timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },

    async smsLogin() {
      try {
        let csrftoken = getCookie('csrftoken');
        if (!csrftoken) {
          await axios.get('/');
          csrftoken = getCookie('csrftoken');
        }

        const response = await axios.post('api/sms-login/', {
          phone_number: this.smsPhone,
          code: this.smsCode
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          // 检查是否需要完成注册（新用户需要选择高考年份）
          if (response.data.need_complete_registration) {
            this.pendingPhone = this.smsPhone;
            this.pendingCode = this.smsCode;
            this.showRegisterModal = true;
          } else {
            await this.fetchUserInfo();
            this.$router.push('/dashboard');
          }
        } else {
          this.error = response.data.error || '登录失败';
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          this.error = err.response.data.error;
        } else {
          this.error = '登录时发生错误，请稍后重试';
        }
      }
    },

    closeRegisterModal() {
      this.showRegisterModal = false;
      this.registerGaokaoYear = '';
      this.pendingPhone = '';
      this.pendingCode = '';
    },

    async completeRegister() {
      if (!this.registerGaokaoYear) {
        this.$message.error('请选择高考年份');
        return;
      }

      try {
        const response = await axios.post('api/complete-sms-registration/', {
          phone_number: this.pendingPhone,
          code: this.pendingCode,
          gaokao_year: parseInt(this.registerGaokaoYear)
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          this.$message.success('注册成功！');
          this.closeRegisterModal();
          await this.fetchUserInfo();
          this.$router.push('/dashboard');
        } else {
          this.$message.error(response.data.error || '注册失败');
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          this.$message.error(err.response.data.error);
        } else {
          this.$message.error('注册失败，请稍后重试');
        }
      }
    },

    async fetchUserInfo() {
      try {
        const response = await axios.get('api/check-login/', {
          withCredentials: true,
          headers: {
            'X-Csrftoken': getCookie('csrftoken')
          },
          credentials: 'include',
        });

        this.$store.commit('SET_USER', {
          isLoggedIn: true,
          username: response.data.username,
          displayName: response.data.displayName || response.data.username,
          isSuperAdmin: response.data.isSuperAdmin
        })
      } catch (err) {
        console.error('获取用户信息错误:', err);
      }
    },

    // 检查是否是从微信授权回调回来的
    async checkWechatCallback() {
      // 从URL参数中获取ticket
      const urlParams = new URLSearchParams(window.location.search);
      const wechatLogin = urlParams.get('wechat_login');
      const ticket = urlParams.get('ticket');
      
      if (wechatLogin === 'success' && ticket) {
        // 是从微信回调回来的，检查登录状态
        this.wechatTicket = ticket;
        this.wechatLoginStatus = 'scanned';
        
        // 显示加载提示
        const loading = this.$message.loading('正在登录...', 0);
        
        // 尝试多次检查登录状态（小屏设备可能需要等待）
        let retryCount = 0;
        const maxRetries = 5;
        
        const checkLogin = async () => {
          try {
            const response = await axios.get('api/wechat/login-status/', {
              params: { ticket: ticket },
              withCredentials: true
            });

            if (response.data.success && response.data.status === 'confirmed') {
              loading.close();
              this.$message.success('微信登录成功！');
              await this.fetchUserInfo();
              // 清除URL参数
              window.history.replaceState({}, document.title, window.location.pathname);
              
              // 检查是否需要完善信息
              if (response.data.need_complete_info) {
                this.isNewUser = true;
                this.showCompleteInfoModal = true;
                this.completeInfoGaokaoYear = '';
                this.completeInfoPhone = '';
              } else {
                // 直接跳转到首页（和扫码登录保持一致）
                this.$router.push('/');
              }
            } else if (retryCount < maxRetries) {
              // 还没确认，稍后重试
              retryCount++;
              setTimeout(checkLogin, 1000);
            } else {
              loading.close();
              this.$message.error('登录超时，请刷新页面重试');
            }
          } catch (err) {
            console.error('检查登录状态失败:', err);
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(checkLogin, 1000);
            } else {
              loading.close();
              this.$message.error('登录失败，请重试');
            }
          }
        };
        
        // 开始检查
        checkLogin();
      }
    },

    // 微信登录相关方法
    async getWechatQrcode() {
      try {
        this.wechatQrcodeUrl = '';
        this.wechatLoginStatus = 'pending';
        
        // 获取当前域名，传递给后端
        const currentDomain = window.location.hostname;
        
        const response = await axios.get('api/wechat/qrcode/', {
          params: { domain: currentDomain },
          withCredentials: true
        });

        if (response.data.success) {
          this.wechatQrcodeUrl = response.data.qrcode_url;
          this.wechatTicket = response.data.ticket;
          this.wechatAuthUrl = response.data.auth_url;
          // 开始轮询登录状态
          this.startWechatLoginCheck();
        } else {
          this.$message.error(response.data.error || '获取二维码失败');
        }
      } catch (err) {
        console.error('获取微信二维码失败:', err);
        this.$message.error('获取微信二维码失败，请稍后重试');
      }
    },

    // 打开微信授权窗口
    openWechatAuth() {
      if (!this.wechatAuthUrl) {
        this.$message.error('授权链接未准备好，请刷新二维码');
        return;
      }
      
      // 检测是否是小屏设备（手机/平板）
      const isMobile = window.innerWidth <= 768 || /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent);
      
      if (isMobile) {
        // 小屏设备：直接在当前页面跳转授权，授权后会自动跳转回来
        window.location.href = this.wechatAuthUrl;
        return;
      }
      
      // 大屏设备：打开弹窗授权
      const width = 600;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      this.wechatAuthWindow = window.open(
        this.wechatAuthUrl,
        'wechat_auth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );
      
      // 监听授权窗口关闭（使用try-catch避免跨域安全错误）
      this.wechatAuthCheckInterval = setInterval(() => {
        try {
          if (this.wechatAuthWindow && this.wechatAuthWindow.closed) {
            clearInterval(this.wechatAuthCheckInterval);
            // 窗口关闭后检查登录状态
            this.checkWechatLoginAfterClose();
          }
        } catch (e) {
          // 跨域访问错误，忽略
        }
      }, 1000);
    },

    // 授权窗口关闭后检查登录状态
    async checkWechatLoginAfterClose() {
      try {
        const response = await axios.get('api/wechat/login-status/', {
          params: { ticket: this.wechatTicket },
          withCredentials: true
        });

        if (response.data.success && response.data.status === 'confirmed') {
          this.$message.success('微信登录成功！');
          await this.fetchUserInfo();
          
          // 检查是否需要完善信息
          if (response.data.need_complete_info) {
            this.isNewUser = true;
            this.showCompleteInfoModal = true;
            this.completeInfoGaokaoYear = '';
            this.completeInfoPhone = '';
          } else {
            this.$router.push('/dashboard');
          }
        }
      } catch (err) {
        console.error('检查登录状态失败:', err);
      }
    },

    startWechatLoginCheck() {
      // 清除之前的定时器
      if (this.wechatCheckInterval) {
        clearInterval(this.wechatCheckInterval);
      }

      // 每2秒检查一次登录状态
      this.wechatCheckInterval = setInterval(async () => {
        try {
          const response = await axios.get('api/wechat/login-status/', {
            params: { ticket: this.wechatTicket },
            withCredentials: true
          });

          if (response.data.success) {
            const status = response.data.status;
            this.wechatLoginStatus = status;

            if (status === 'confirmed') {
              // 登录成功
              clearInterval(this.wechatCheckInterval);
              // 关闭授权窗口（如果还开着）
              if (this.wechatAuthWindow && !this.wechatAuthWindow.closed) {
                this.wechatAuthWindow.close();
              }
              clearInterval(this.wechatAuthCheckInterval);
              this.$message.success('微信登录成功！');
              await this.fetchUserInfo();
              
              // 检查是否需要完善信息
              if (response.data.need_complete_info) {
                this.isNewUser = true;
                this.showCompleteInfoModal = true;
                this.completeInfoGaokaoYear = '';
                this.completeInfoPhone = '';
              } else {
                this.$router.push('/dashboard');
              }
            } else if (status === 'expired') {
              // 二维码过期
              clearInterval(this.wechatCheckInterval);
              clearInterval(this.wechatAuthCheckInterval);
              this.$message.warning('二维码已过期，请刷新');
            }
          }
        } catch (err) {
          console.error('检查登录状态失败:', err);
        }
      }, 2000);
    },

    refreshWechatQrcode() {
      // 关闭之前的授权窗口
      if (this.wechatAuthWindow && !this.wechatAuthWindow.closed) {
        this.wechatAuthWindow.close();
      }
      clearInterval(this.wechatAuthCheckInterval);
      this.getWechatQrcode();
    },

    // 关闭完善信息弹窗
    closeCompleteInfoModal() {
      this.showCompleteInfoModal = false;
    },

    // 提交完善信息
    async submitCompleteInfo() {
      // 验证高考年份
      if (!this.completeInfoGaokaoYear) {
        this.$message.error('请选择高考年份');
        return;
      }

      // 验证手机号
      if (!this.completeInfoPhone || !/^1[3-9]\d{9}$/.test(this.completeInfoPhone)) {
        this.$message.error('请输入正确的手机号');
        return;
      }

      try {
        const response = await axios.post('api/wechat/complete-info/', {
          gaokao_year: this.completeInfoGaokaoYear,
          phone: this.completeInfoPhone
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          this.$message.success('信息完善成功！');
          this.showCompleteInfoModal = false;
          // 更新用户信息
          await this.fetchUserInfo();
          // 显示设置密码弹窗
          this.showSetPasswordModal = true;
          this.setPasswordValue = '';
          this.confirmPasswordValue = '';
        } else {
          this.$message.error(response.data.error || '保存失败');
        }
      } catch (err) {
        console.error('完善信息失败:', err);
        this.$message.error(err.response?.data?.error || '保存失败，请稍后重试');
      }
    },

    // 跳过设置密码
    skipSetPassword() {
      this.showSetPasswordModal = false;
      this.setPasswordValue = '';
      this.confirmPasswordValue = '';
      // 跳转到首页
      this.$router.push('/dashboard');
    },

    // 提交设置密码
    async submitSetPassword() {
      // 验证密码
      if (!this.setPasswordValue || this.setPasswordValue.length < 6) {
        this.$message.error('密码长度至少6位');
        return;
      }

      if (this.setPasswordValue !== this.confirmPasswordValue) {
        this.$message.error('两次输入的密码不一致');
        return;
      }

      try {
        const response = await axios.post('api/set-password/', {
          password: this.setPasswordValue,
          confirm_password: this.confirmPasswordValue
        }, {
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'X-Csrftoken',
        });

        if (response.data.success) {
          this.$message.success('密码设置成功！下次可用手机号和密码登录');
          this.showSetPasswordModal = false;
          this.setPasswordValue = '';
          this.confirmPasswordValue = '';
          // 跳转到首页
          this.$router.push('/dashboard');
        } else {
          this.$message.error(response.data.error || '设置失败');
        }
      } catch (err) {
        console.error('设置密码失败:', err);
        this.$message.error(err.response?.data?.error || '设置失败，请稍后重试');
      }
    },
  },
  
  watch: {
    loginType(newVal) {
      if (newVal === 'wechat') {
        this.getWechatQrcode();
      } else {
        // 清除轮询
        if (this.wechatCheckInterval) {
          clearInterval(this.wechatCheckInterval);
        }
      }
    }
  },
  
  beforeDestroy() {
    // 清除轮询定时器
    if (this.wechatCheckInterval) {
      clearInterval(this.wechatCheckInterval);
    }
    // 移除窗口大小变化监听器
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
.login-wrapper {
  /* 使用flex布局实现垂直和水平居中 */
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh; /* 确保占满整个视口高度 */
  background-color: #f5f5f5; /* 设置背景色 */
}

.login-container {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 350px; /* 增加宽度 */
  text-align: center; /* 文本居中 */
}

h2 {
  margin-bottom: 1.5rem;
  color: #409EFF;
  white-space: normal;
  font-size: 1.5rem;
}

.login-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e4e7ed;
}

.tab-btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  color: #606266;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #409EFF;
}

.tab-btn.active {
  color: #409EFF;
  border-bottom: 2px solid #409EFF;
  margin-bottom: -2px;
}

.tab-btn:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
  background-color: #f5f7fa;
}

.tab-btn:disabled:hover {
  color: #c0c4cc;
  background-color: #f5f7fa;
}

.form-group {
  margin-bottom: 1rem;
  text-align: left; /* 表单文本左对齐 */
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #606266;
}

input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s;
}

input:focus {
  outline: none;
  border-color: #409EFF;
}

.sms-code-group {
  display: flex;
  gap: 0.5rem;
}

.sms-code-group input {
  flex: 1;
}

.send-code-btn {
  padding: 0.75rem 1rem;
  background-color: #fff;
  color: #409EFF;
  border: 1px solid #409EFF;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 0.3s;
}

.send-code-btn:hover:not(:disabled) {
  background-color: #ecf5ff;
}

.send-code-btn:disabled {
  color: #c0c4cc;
  border-color: #e4e7ed;
  cursor: not-allowed;
}

.btn {
  width: 100%;
  padding: 0.75rem;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
}

.btn:hover {
  background-color: #66b1ff;
}

.error {
  color: #f56c6c;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background-color: #fef0f0;
  border-radius: 4px;
}

.link {
  margin-top: 1rem;
  color: #606266;
}

.link a {
  color: #409EFF;
  text-decoration: none;
}

.link a:hover {
  text-decoration: underline;
}

/* 注册弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-container h3 {
  margin-bottom: 1.5rem;
  color: #409EFF;
  text-align: center;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  line-height: 1.5;
  height: auto;
  min-height: 2.5rem;
  transition: border-color 0.3s;
  background-color: #fff;
}

.form-select:focus {
  outline: none;
  border-color: #409EFF;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #409EFF;
}

.modal-desc {
  text-align: center;
  color: #606266;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.hint-text {
  font-size: 0.85rem;
  color: #909399;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.modal-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
}

.btn-cancel {
  background-color: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
}

.btn-cancel:hover {
  background-color: #ecf5ff;
  border-color: #409EFF;
  color: #409EFF;
}

.btn-confirm {
  background-color: #409EFF;
  color: white;
}

.btn-confirm:hover {
  background-color: #66b1ff;
}

/* 微信登录样式 */
.wechat-login {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
}

.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
}



.qrcode-img {
  width: 200px;
  height: 200px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  background-color: white;
  transition: opacity 0.3s;
}







.qrcode-tip {
  margin-top: 1rem;
  color: #606266;
  font-size: 14px;
}

.status-text {
  margin-top: 0.5rem;
  font-size: 14px;
  font-weight: bold;
}

.status-text.scanned {
  color: #409EFF;
}

.status-text.expired {
  color: #f56c6c;
}

.qrcode-loading {
  padding: 2rem;
  color: #909399;
}

.btn-refresh {
  background-color: #67c23a;
  margin-top: 1rem;
}

.btn-refresh:hover {
  background-color: #85ce61;
}

.btn-auth {
  background-color: #07c160;
  margin-top: 1rem;
  width: 100%;
  max-width: 200px;
}

.btn-auth:hover {
  background-color: #06ad56;
}

.qrcode-image {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}
</style>